import { ConsultingAstrologerBasicReport } from '../types/consultingReport';
import { UserProfile, UserRole } from '../types/user';
import { recordAuditLog } from './authorizationEngine';

const REPORTS_STORAGE_KEY = 'vedang_consulting_reports_v1';

export function getConsultingReports(currentUser: UserProfile | null): ConsultingAstrologerBasicReport[] {
  if (!currentUser) return [];

  try {
    const raw = localStorage.getItem(REPORTS_STORAGE_KEY);
    const reports: ConsultingAstrologerBasicReport[] = raw ? JSON.parse(raw) : [];

    // Super Admin & Management can see all or filter
    if (currentUser.role === UserRole.SUPER_ADMIN || currentUser.role === UserRole.ADMIN || currentUser.activeRole === ('MANAGEMENT' as any) || (currentUser.role as any) === 'MANAGEMENT') {
      return reports;
    }

    // Consulting Astrologer sees only their own reports
    if (currentUser.role === UserRole.CONSULTING_ASTROLOGER) {
      return reports.filter((r) => r.consultantAstrologerId === currentUser.uid);
    }

    // Basic User sees reports explicitly where clientReferenceId matches their uid or profile
    if (currentUser.role === UserRole.BASIC_USER) {
      return reports.filter((r) => r.clientReferenceId === currentUser.uid);
    }

    // Tester can view reports in test mode
    if (currentUser.isTester) {
      return reports;
    }

    return [];
  } catch (e) {
    console.warn('Error fetching consulting reports:', e);
    return [];
  }
}

export function saveConsultingReport(
  currentUser: UserProfile,
  reportData: Omit<ConsultingAstrologerBasicReport, 'reportId' | 'createdAt' | 'updatedAt' | 'integrityHash'>
): ConsultingAstrologerBasicReport {
  if (currentUser.role !== UserRole.CONSULTING_ASTROLOGER && currentUser.role !== UserRole.SUPER_ADMIN && currentUser.role !== UserRole.TESTER) {
    recordAuditLog({
      eventType: 'AUTHZ_DENIED',
      user: currentUser,
      resourceType: 'consultation',
      action: 'SAVE_CONSULTING_REPORT',
      decision: 'DENY',
      details: 'Unauthorized attempt to create/edit Consulting Astrologer Basic Report.'
    });
    throw new Error('Unauthorized: Only authorized Consulting Astrologers or Super Admin can create basic reports.');
  }

  const reportId = `rep_cns_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
  const now = new Date().toISOString();

  const rawStr = `${reportId}|${reportData.consultationId}|${currentUser.uid}|${now}`;
  let hash = 0;
  for (let i = 0; i < rawStr.length; i++) {
    hash = (hash << 5) - hash + rawStr.charCodeAt(i);
    hash |= 0;
  }
  const integrityHash = `HASH_REP_${Math.abs(hash).toString(16).toUpperCase()}`;

  const fullReport: ConsultingAstrologerBasicReport = {
    ...reportData,
    reportId,
    consultantAstrologerId: reportData.consultantAstrologerId || currentUser.uid,
    consultantAstrologerName: reportData.consultantAstrologerName || currentUser.displayName || currentUser.email,
    createdAt: now,
    updatedAt: now,
    integrityHash
  };

  try {
    const raw = localStorage.getItem(REPORTS_STORAGE_KEY);
    const reports: ConsultingAstrologerBasicReport[] = raw ? JSON.parse(raw) : [];
    const existingIndex = reports.findIndex((r) => r.consultationId === fullReport.consultationId);

    if (existingIndex >= 0) {
      reports[existingIndex] = {
        ...fullReport,
        reportId: reports[existingIndex].reportId,
        createdAt: reports[existingIndex].createdAt,
        updatedAt: now
      };
    } else {
      reports.unshift(fullReport);
    }

    localStorage.setItem(REPORTS_STORAGE_KEY, JSON.stringify(reports));

    recordAuditLog({
      eventType: 'CONSULTANT_ACCESS',
      user: currentUser,
      resourceType: 'consultation',
      resourceId: reportId,
      action: 'SAVE_CONSULTING_REPORT',
      decision: 'ALLOW',
      details: `Saved Consulting Astrologer Basic Report [${reportId}] for consultation [${fullReport.consultationId}]`
    });
  } catch (e) {
    console.error('Error saving consulting report:', e);
  }

  return fullReport;
}
